import { useState, useEffect } from "react";
import { Maximize2, Sun, Cloud, CloudRain, Wind, CloudSun, Thermometer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StaticSatelliteMap = ({ sensors = [] }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [weather, setWeather] = useState(null);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axios.get(`${API}/weather/current`);
        setWeather(response.data);
      } catch (error) {
        // Use mock data if API fails
        setWeather({
          temperature: 22,
          description: "Soleado",
          icon: "sun",
          humidity: 65,
          wind_speed: 12,
          wind_direction: "NE"
        });
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000); // Update every 10 min
    return () => clearInterval(interval);
  }, []);

  const getSensorData = (name) => {
    const sensor = sensors.find(s => s.name.toUpperCase().includes(name.toUpperCase()));
    return {
      level: sensor?.current_level || 0,
      status: sensor?.status || 'normal',
    };
  };

  const parralillo = getSensorData('PARRALILLO');
  const siberio = getSensorData('SIBERIO');
  const caidero = getSensorData('CAIDERO');

  const hasOverflow = parralillo.status === 'overflow' || siberio.status === 'overflow' || caidero.status === 'overflow';

  // Weather icon component
  const WeatherIcon = ({ icon, size = "w-5 h-5" }) => {
    switch (icon) {
      case 'sun':
      case 'clear':
        return <Sun className={`${size} text-yellow-400`} />;
      case 'cloud':
      case 'cloudy':
        return <Cloud className={`${size} text-gray-400`} />;
      case 'rain':
      case 'rainy':
        return <CloudRain className={`${size} text-blue-400`} />;
      case 'partly-cloudy':
      case 'partial':
        return <CloudSun className={`${size} text-yellow-300`} />;
      default:
        return <Sun className={`${size} text-yellow-400`} />;
    }
  };

  const MapContent = ({ size = 'small' }) => {
    const isLarge = size === 'large';
    
    return (
      <div className="relative w-full h-full overflow-hidden rounded-lg bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950">
        {/* Mapa base con tiles de OpenStreetMap */}
        <div 
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `url('https://tile.openstreetmap.org/12/2044/1634.png'), url('https://tile.openstreetmap.org/12/2045/1634.png')`,
            backgroundSize: '256px 256px',
            backgroundPosition: 'center',
            filter: 'saturate(0.3) brightness(0.5)'
          }}
        />
        
        {/* Fondo alternativo con gradiente de terreno */}
        <div className="absolute inset-0">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="terrainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1e3a5f" />
                <stop offset="50%" stopColor="#2d4a3e" />
                <stop offset="100%" stopColor="#1a2f23" />
              </linearGradient>
              <linearGradient id="oceanGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0c4a6e" />
                <stop offset="100%" stopColor="#082f49" />
              </linearGradient>
            </defs>
            {/* Terreno montañoso */}
            <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#terrainGrad)" />
            {/* Océano en la esquina inferior izquierda */}
            <path d="M0,60 Q15,55 5,85 L0,100 Z" fill="url(#oceanGrad)" opacity="0.8" />
          </svg>
        </div>
        
        {/* SVG para el flujo del agua y marcadores */}
        <svg 
          className="absolute inset-0 w-full h-full" 
          viewBox="0 0 100 100" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waterGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8"/>
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.9"/>
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Flujo del agua - Barranco principal hacia el mar */}
          <path
            d={`M 75 12 C 70 20, 60 30, 50 45 C 40 55, 25 65, 8 78 L 5 82`}
            fill="none"
            stroke="url(#waterGradient)"
            strokeWidth={isLarge ? "1.5" : "2"}
            strokeLinecap="round"
            strokeDasharray={hasOverflow ? "none" : "4,2"}
            filter="url(#glow)"
            className={hasOverflow ? "animate-pulse" : ""}
          />
          
          {/* Ramal desde SIBERIO */}
          <path
            d="M 82 25 C 75 30, 65 38, 50 45"
            fill="none"
            stroke="#64748b"
            strokeWidth="1"
            strokeDasharray="3,2"
            opacity="0.6"
          />
          
          {/* Ramal desde CAIDERO */}
          <path
            d="M 88 50 C 75 52, 60 48, 50 45"
            fill="none"
            stroke="#64748b"
            strokeWidth="1"
            strokeDasharray="3,2"
            opacity="0.6"
          />
          
          {/* Marcador del océano */}
          <circle cx="5" cy="82" r={isLarge ? "3" : "4"} fill="#0ea5e9" opacity="0.8">
            <animate attributeName="r" values={isLarge ? "3;4;3" : "4;5;4"} dur="2s" repeatCount="indefinite"/>
          </circle>
        </svg>
        
        {/* Marcadores de las presas */}
        {/* PARRALILLO */}
        <div 
          className="absolute flex flex-col items-center"
          style={{ top: '10%', left: '72%', transform: 'translate(-50%, -50%)' }}
        >
          <div className={`
            ${isLarge ? 'w-14 h-14 text-sm' : 'w-10 h-10 text-xs'}
            rounded-full flex items-center justify-center font-bold text-white
            ${parralillo.status === 'overflow' ? 'bg-red-500 animate-pulse ring-4 ring-red-500/50' : 'bg-emerald-500'}
            shadow-lg border-2 border-white/20
          `}>
            {parralillo.level.toFixed(2)}m
          </div>
          {isLarge && <span className="text-white text-xs mt-1 font-semibold drop-shadow-lg bg-black/40 px-1 rounded">PARRALILLO</span>}
        </div>
        
        {/* SIBERIO */}
        <div 
          className="absolute flex flex-col items-center"
          style={{ top: '28%', left: '80%', transform: 'translate(-50%, -50%)' }}
        >
          <div className={`
            ${isLarge ? 'w-14 h-14 text-sm' : 'w-10 h-10 text-xs'}
            rounded-full flex items-center justify-center font-bold text-white
            ${siberio.status === 'overflow' ? 'bg-red-500 animate-pulse ring-4 ring-red-500/50' : 'bg-emerald-500'}
            shadow-lg border-2 border-white/20
          `}>
            {siberio.level.toFixed(2)}m
          </div>
          {isLarge && <span className="text-white text-xs mt-1 font-semibold drop-shadow-lg bg-black/40 px-1 rounded">SIBERIO</span>}
        </div>
        
        {/* CAIDERO */}
        <div 
          className="absolute flex flex-col items-center"
          style={{ top: '52%', left: '85%', transform: 'translate(-50%, -50%)' }}
        >
          <div className={`
            ${isLarge ? 'w-14 h-14 text-sm' : 'w-10 h-10 text-xs'}
            rounded-full flex items-center justify-center font-bold text-white
            ${caidero.status === 'overflow' ? 'bg-red-500 animate-pulse ring-4 ring-red-500/50' : 'bg-emerald-500'}
            shadow-lg border-2 border-white/20
          `}>
            {caidero.level.toFixed(2)}m
          </div>
          {isLarge && <span className="text-white text-xs mt-1 font-semibold drop-shadow-lg bg-black/40 px-1 rounded">CAIDERO</span>}
        </div>
        
        {/* Widget del Tiempo - Esquina superior izquierda */}
        {weather && (
          <div className={`absolute ${isLarge ? 'top-3 left-3' : 'top-1 left-1'} bg-black/70 backdrop-blur-sm rounded-lg ${isLarge ? 'p-3' : 'p-1.5'} border border-white/10`}>
            <div className="flex items-center gap-2">
              <WeatherIcon icon={weather.icon} size={isLarge ? "w-8 h-8" : "w-4 h-4"} />
              <div>
                <div className={`text-white font-bold ${isLarge ? 'text-xl' : 'text-xs'}`}>
                  {weather.temperature}°C
                </div>
                {isLarge && (
                  <div className="text-zinc-400 text-xs">{weather.description}</div>
                )}
              </div>
              {isLarge && (
                <div className="ml-3 pl-3 border-l border-zinc-600">
                  <div className="flex items-center gap-1 text-zinc-300 text-xs">
                    <Wind className="w-3 h-3" />
                    <span>{weather.wind_speed} km/h {weather.wind_direction}</span>
                  </div>
                  <div className="flex items-center gap-1 text-zinc-300 text-xs">
                    <Thermometer className="w-3 h-3" />
                    <span>Humedad: {weather.humidity}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Etiqueta La Aldea */}
        {isLarge && (
          <div 
            className="absolute text-white text-sm font-bold drop-shadow-lg bg-black/40 px-2 py-1 rounded"
            style={{ bottom: '18%', left: '8%' }}
          >
            La Aldea de San Nicolás
          </div>
        )}
        
        {/* Etiqueta Océano */}
        <div 
          className={`absolute text-cyan-300 font-bold drop-shadow-lg ${isLarge ? 'text-base' : 'text-xs'}`}
          style={{ bottom: isLarge ? '12%' : '8%', left: isLarge ? '3%' : '2%' }}
        >
          OCÉANO
        </div>
        
        {/* Leyenda */}
        <div className={`absolute ${isLarge ? 'bottom-4 right-4' : 'bottom-1 right-1'} bg-black/70 rounded px-2 py-1`}>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className={`${isLarge ? 'w-3 h-3' : 'w-2 h-2'} rounded-full bg-emerald-500`}></div>
              <span className={`text-white ${isLarge ? 'text-xs' : 'text-[8px]'}`}>Normal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={`${isLarge ? 'w-3 h-3' : 'w-2 h-2'} rounded-full bg-red-500 animate-pulse`}></div>
              <span className={`text-white ${isLarge ? 'text-xs' : 'text-[8px]'}`}>Alerta</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="h-full w-full rounded-lg overflow-hidden relative group" style={{ minHeight: '120px' }}>
        <MapContent size="small" />
        
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <Button 
            size="icon" 
            variant="ghost" 
            className="h-6 w-6 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => setFullscreen(true)}
            data-testid="map-maximize-btn"
          >
            <Maximize2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] w-full bg-zinc-950 border-zinc-800 p-4">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Mapa - Sistema Hidrológico La Aldea
              {hasOverflow && (
                <span className="text-red-400 animate-pulse text-xs ml-2 px-2 py-1 bg-red-500/20 rounded-full">
                  DESBORDE ACTIVO
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-4" style={{ height: 'calc(80vh - 60px)' }}>
            <div className="flex-1 rounded-xl overflow-hidden">
              <MapContent size="large" />
            </div>
            
            <div className="w-64 bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
              <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-4">
                Niveles de Agua
              </h3>
              
              {[
                { name: 'PARRALILLO', data: parralillo },
                { name: 'SIBERIO', data: siberio },
                { name: 'CAIDERO', data: caidero },
              ].map(({ name, data }) => (
                <div key={name} className="mb-3 p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-zinc-300">{name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      data.status === 'overflow' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {data.status === 'overflow' ? 'DESBORDE' : 'NORMAL'}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-white font-mono">
                    {data.level.toFixed(2)} <span className="text-sm text-zinc-500">m</span>
                  </div>
                </div>
              ))}
              
              {/* Weather Section */}
              {weather && (
                <div className="border-t border-zinc-700 pt-4 mt-4">
                  <h4 className="text-[10px] font-semibold text-zinc-500 uppercase mb-2">
                    Tiempo Actual
                  </h4>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <WeatherIcon icon={weather.icon} size="w-10 h-10" />
                      <div>
                        <div className="text-2xl font-bold text-white">{weather.temperature}°C</div>
                        <div className="text-xs text-zinc-400">{weather.description}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div className="flex items-center gap-1 text-zinc-400">
                        <Wind className="w-3 h-3" />
                        <span>{weather.wind_speed} km/h</span>
                      </div>
                      <div className="flex items-center gap-1 text-zinc-400">
                        <Thermometer className="w-3 h-3" />
                        <span>{weather.humidity}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="border-t border-zinc-700 pt-4 mt-4">
                <h4 className="text-[10px] font-semibold text-zinc-500 uppercase mb-2">
                  Flujo del Barranco
                </h4>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  El agua fluye desde las presas por el Barranco de La Aldea 
                  hasta desembocar en el Océano Atlántico (~15 km de recorrido).
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StaticSatelliteMap;
